import React, { useState, useEffect } from "react";
import useThemeHook from "../hooks/useThemeHook";
import { useTranslationHook } from "../hooks/useTranslationHook";
import { Video, Crown, Check } from "lucide-react";
import arthereumProvider from "../../backend/ArthereumProvider";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../redux";
import piNetworkAdsManager from "../utils/PiNetworkAdsManager";
import { SKIPS_PER_DAY } from "../utils/rewardTables";
import isProUser from "../utils/isProUser";

const todayKey = () => new Date().toISOString().split("T")[0];

const GrantNotAvailableCard = ({
  nextGrantDate = new Date(
    new Date().getTime() + 2 * 60 * 60 * 1000
  ).toDateString(),
  targetDate,
}: {
  nextGrantDate?: any;
  targetDate: any;
}) => {
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ text: string; kind: "info" | "error" | "success" } | null>(
    null
  );
  const [timeRemaining, setTimeRemaining] = useState("");
  const theme = useThemeHook();
  const getTranslationText = useTranslationHook();

  const today = todayKey();
  const skipsUsedToday = user?.skipsTodayDate === today ? user?.skipsTodayCount || 0 : 0;
  const skipsLeft = Math.max(0, SKIPS_PER_DAY - skipsUsedToday);
  /** The n-th skip of the day costs n ads. */
  const required = skipsUsedToday + 1;
  const isPro = isProUser(user);
  const proSkipAvailable = isPro && skipsUsedToday === 0;

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        setTimeRemaining("Grant Available!");
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining(`${days}:${hours}:${minutes}:${seconds}`);
    };

    const timer = setInterval(calculateTimeRemaining, 1000);
    calculateTimeRemaining();

    return () => clearInterval(timer);
  }, [targetDate]);

  /** Applies a completed skip to Redux so the Home card unlocks immediately. */
  const applySkip = (skipsToday: number) => {
    dispatch(
      actions.setActualUser({
        ...userData,
        user: {
          ...user,
          lastMiningDate: null,
          skipsTodayDate: today,
          skipsTodayCount: skipsToday,
        },
        miningInfos: {
          ...userData?.miningInfos,
          nextGrantDate: new Date(),
        },
      })
    );
  };

  const handleWatchAdsToSkip = async () => {
    if (loading || !user?.id || skipsLeft <= 0) return;

    setLoading(true);
    setStatus({ text: `Watching ad 1 of ${required}…`, kind: "info" });

    let watched = 0;
    try {
      while (watched < required) {
        let rewarded = false;
        // onSuccess only fires for a verified rewarded ad.
        // eslint-disable-next-line no-await-in-loop
        await piNetworkAdsManager.handleRewardedAdFlowFront(user.id, () => {
          rewarded = true;
        });
        if (!rewarded) break;
        watched += 1;
        if (watched < required) {
          setStatus({ text: `Watching ad ${watched + 1} of ${required}…`, kind: "info" });
        }
      }

      if (watched < required) {
        setStatus({
          text: `Only ${watched} of ${required} ads were verified — the cooldown was not skipped.`,
          kind: "error",
        });
        return;
      }

      const { skipsToday } = await arthereumProvider.instantRefill(user.id, "ads");
      applySkip(skipsToday);
      setStatus({ text: "Cooldown skipped — your grant is ready now.", kind: "success" });
    } catch (error: any) {
      console.error("Skip the wait failed:", error);
      setStatus({ text: error?.message || "Could not skip the cooldown.", kind: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleProSkip = async () => {
    if (loading || !user?.id) return;
    setLoading(true);
    setStatus(null);
    try {
      const { skipsToday } = await arthereumProvider.instantRefill(user.id, "pro");
      applySkip(skipsToday);
      setStatus({ text: "Free Pro skip used — your grant is ready now.", kind: "success" });
    } catch (error: any) {
      console.error("Pro skip failed:", error);
      setStatus({ text: error?.message || "Could not use the free Pro skip.", kind: "error" });
    } finally {
      setLoading(false);
    }
  };

  const statusColor =
    status?.kind === "error"
      ? "text-red-500"
      : status?.kind === "success"
      ? "text-emerald-500"
      : theme === "dark"
      ? "text-gray-300"
      : "text-gray-500";

  return (
    <div className="bg-transparent border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full bg-${theme === "dark" ? "orange-500" : "amber-100"}`}>
            <span className={`text-lg font-semibold text-${theme === "dark" ? "white" : "amber-600"}`}>G</span>
          </div>

          <div>
            <p className={`text-sm text-${theme === "dark" ? "gray-300" : "gray-500"}`}>{getTranslationText("next_grant")}</p>
            <p className={`text-lg font-semibold text-${theme === "dark" ? "gray-100" : "gray-900"}`}>{nextGrantDate}</p>
          </div>
        </div>

        {/* The countdown stays visible even when skips are used up. */}
        <p className={`text-base font-medium text-${theme === "dark" ? "orange-500" : "amber-600"}`}>{timeRemaining}</p>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
        {skipsLeft <= 0 ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Check size={14} className="text-amber-500" />
            <span>
              Skips used up for today ({SKIPS_PER_DAY}/{SKIPS_PER_DAY}) — the countdown above still runs.
            </span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Video size={14} className="text-amber-500" />
                <span>
                  Skip the wait · {skipsLeft} of {SKIPS_PER_DAY} skips left today
                </span>
              </div>
              <button
                onClick={handleWatchAdsToSkip}
                disabled={loading}
                className="bg-amber-100 hover:bg-amber-200 disabled:opacity-60 text-amber-700 px-4 py-2 rounded-xl text-xs font-bold transition-all active:scale-95 flex items-center gap-2 shrink-0"
              >
                {loading
                  ? "Loading ad…"
                  : required === 1
                  ? "Watch 1 ad to skip"
                  : `Watch ${required} ads to skip`}
              </button>
            </div>

            {proSkipAvailable && (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-amber-600">
                  <Crown size={14} className="text-amber-500" />
                  <span>Pro perk: one free skip per day</span>
                </div>
                <button
                  onClick={handleProSkip}
                  disabled={loading}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 disabled:opacity-60 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all active:scale-95 shrink-0"
                >
                  Free Pro skip
                </button>
              </div>
            )}
          </>
        )}

        {status && <p className={`text-xs font-semibold ${statusColor}`}>{status.text}</p>}
      </div>
    </div>
  );
};

export default GrantNotAvailableCard;
